import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Disclaimer } from "@/components/Common";
import { ChevronDown, ChevronRight, Filter, RefreshCcw, ShieldCheck, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";

// MP8 — Audit Log:
// • Filters: date range, user, action_type, record_type, record_id
// • Expandable detail row showing before_value / after_value JSON + notes
// • Internal accountability language; no public exposure
//
// Action type humanization is purely cosmetic — backend keeps dotted names.

const RECORD_TYPES = [
  "all", "track", "document", "license_request", "submission",
  "compliance_review", "ai_review_note", "user", "settings",
];

function humanize(s) {
  if (!s) return "—";
  return s.replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Phase A Run 2 — small helpers for the tiered enriched UI. Legacy rows
// (no severity / no material flag) render neutrally; enriched rows get a
// visible badge so operators can spot high-signal events in the timeline.
function SeverityBadge({ value }) {
  if (!value || value === "info") return null;
  const s = String(value).toLowerCase();
  const style =
    s === "critical" ? { className: "bg-red-100 text-red-900 border-red-300", label: "Critical" } :
    s === "high"     ? { className: "bg-amber-100 text-amber-900 border-amber-300", label: "High" } :
    s === "low"      ? { className: "bg-slate-100 text-slate-800 border-slate-300", label: "Low" } :
                        null;
  if (!style) return null;
  return <Badge variant="outline" className={`text-[10px] ${style.className}`} data-testid={`severity-${s}`}>{style.label}</Badge>;
}

function MaterialBadge({ material }) {
  if (!material) return null;
  return (
    <Badge variant="outline" className="text-[10px] bg-[hsl(347_72%_22%)]/10 text-[hsl(347_72%_22%)] border-[hsl(347_72%_22%)]/40 ml-1" data-testid="material-badge">
      <ShieldAlert className="w-3 h-3 mr-0.5" /> Material
    </Badge>
  );
}

function fmt(v) {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

const SEVERITY_OPTIONS = ["all", "info", "low", "high", "critical"];

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [recordType, setRecordType] = useState("all");
  const [actionType, setActionType] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [recordId, setRecordId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  // Phase A Run 2 — tiered filters (severity + material-only)
  const [severity, setSeverity] = useState("all");
  const [materialOnly, setMaterialOnly] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (recordType !== "all") params.record_type = recordType;
      if (actionType.trim()) params.action_type = actionType.trim();
      if (userEmail.trim()) params.user_email = userEmail.trim();
      if (recordId.trim()) params.record_id = recordId.trim();
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await api.get("/audit", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordType]);

  function reset() {
    setRecordType("all"); setActionType(""); setUserEmail(""); setRecordId("");
    setStartDate(""); setEndDate(""); setExpanded(new Set());
    setSeverity("all"); setMaterialOnly(false);
    setTimeout(load, 0);
  }

  function toggleExpand(id) {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  }

  // Build distinct action_type options from visible items for a quick datalist.
  const actionOptions = useMemo(
    () => Array.from(new Set(items.map(a => a.action_type).filter(Boolean))).sort(),
    [items],
  );

  // Phase A Run 2 — client-side filter for severity + material.
  // These live on top of the server filters so operators can quickly slice
  // the current result set without extra round-trips.
  const filteredItems = useMemo(() => {
    return items.filter((a) => {
      if (materialOnly && !a.material) return false;
      if (severity !== "all") {
        const s = (a.severity || "info").toLowerCase();
        if (s !== severity) return false;
      }
      return true;
    });
  }, [items, severity, materialOnly]);

  return (
    <div data-testid="audit-log-page">
      <PageHeader
        title="Audit Log"
        subtitle="Internal activity history for compliance accountability and review."
      />

      <Card className="mb-4">
        <CardContent className="p-4 grid lg:grid-cols-6 md:grid-cols-3 sm:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground">Record type</label>
            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RECORD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Action type</label>
            <Input value={actionType} onChange={e => setActionType(e.target.value)} placeholder="e.g. submission.status_changed" list="audit-action-options" data-testid="audit-action-filter" />
            <datalist id="audit-action-options">
              {actionOptions.map(o => <option key={o} value={o} />)}
            </datalist>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">User email</label>
            <Input value={userEmail} onChange={e => setUserEmail(e.target.value)} placeholder="admin@gfmp.example.com" data-testid="audit-user-filter" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Record ID</label>
            <Input value={recordId} onChange={e => setRecordId(e.target.value)} placeholder="exact UUID or id" className="font-mono text-xs" data-testid="audit-recordid-filter" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Start date</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="audit-start-date" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">End date</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="audit-end-date" />
          </div>
          <div className="lg:col-span-6 sm:col-span-2 flex flex-wrap justify-end items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Severity</label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="h-8 w-[120px]" data-testid="audit-severity-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(o => <SelectItem key={o} value={o}>{o === "all" ? "All severities" : o[0].toUpperCase() + o.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="audit-material-toggle" checked={materialOnly} onCheckedChange={setMaterialOnly} data-testid="audit-material-toggle" />
              <label htmlFor="audit-material-toggle" className="text-xs text-muted-foreground cursor-pointer">Material only</label>
            </div>
            <Button variant="outline" size="sm" onClick={reset} data-testid="audit-reset"><RefreshCcw className="w-4 h-4 mr-2" />Reset</Button>
            <Button size="sm" onClick={load} data-testid="audit-apply" style={{ background: "hsl(347 72% 22%)" }}><Filter className="w-4 h-4 mr-2" />Apply</Button>
            <span className="text-xs text-muted-foreground self-center" data-testid="audit-count">{filteredItems.length} / {items.length} entries</span>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4 border-[hsl(40_48%_32%)]/30">
        <CardContent className="p-3 flex items-start gap-2 text-xs" data-testid="audit-legal-note">
          <ShieldCheck className="w-4 h-4 mt-0.5" style={{ color: "hsl(40 48% 32%)" }} />
          <div>
            Audit records support internal accountability and compliance review. They do not constitute legal advice or final legal clearance.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Record type</TableHead>
                <TableHead>Record ID</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Notes / Summary</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && filteredItems.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No audit entries match these filters.</TableCell></TableRow>
              )}
              {filteredItems.map(a => {
                const open = expanded.has(a.audit_id);
                const displaySummary = a.summary || formatNotes(a.notes, { fallback: "" });
                return (
                  <React.Fragment key={a.audit_id}>
                    <TableRow className="cursor-pointer hover:bg-secondary/40" onClick={() => toggleExpand(a.audit_id)} data-testid={`audit-row-${a.audit_id}`}>
                      <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{(a.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                      <TableCell className="text-sm">
                        <div>{a.user_name || a.user_email || "system"}</div>
                        {a.user_email && a.user_name && <div className="text-[11px] text-muted-foreground">{a.user_email}</div>}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        <span>{humanize(a.action_type)}</span>
                        <MaterialBadge material={a.material} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {a.module ? <div>{a.module}</div> : <span>—</span>}
                        {a.screen && <div className="text-[10px] opacity-70">{a.screen}</div>}
                      </TableCell>
                      <TableCell className="text-xs">{a.record_type}</TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">{a.record_id ? a.record_id.slice(0, 12) + (a.record_id.length > 12 ? "…" : "") : "—"}</TableCell>
                      <TableCell><SeverityBadge value={a.severity} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate" title={displaySummary}>{displaySummary || "—"}</TableCell>
                    </TableRow>
                    {open && (
                      <TableRow data-testid={`audit-detail-${a.audit_id}`}>
                        <TableCell></TableCell>
                        <TableCell colSpan={8} className="bg-secondary/30">
                          <div className="grid md:grid-cols-2 gap-3 py-2 text-xs">
                            <DetailField label="Audit ID" value={<span className="font-mono">{a.audit_id}</span>} />
                            <DetailField label="Action (raw)" value={<span className="font-mono">{a.action_type}</span>} />
                            <DetailField label="User ID" value={<span className="font-mono">{a.user_id || "—"}</span>} />
                            <DetailField label="User email" value={<span className="font-mono">{a.user_email || "—"}</span>} />
                            <DetailField label="Record ID (full)" value={<span className="font-mono break-all">{a.record_id || "—"}</span>} />
                            <DetailField label="Timestamp" value={<span className="font-mono">{a.timestamp}</span>} />
                            {a.module && <DetailField label="Module" value={a.module} />}
                            {a.screen && <DetailField label="Screen" value={<span className="font-mono">{a.screen}</span>} />}
                            {a.severity && <DetailField label="Severity" value={<SeverityBadge value={a.severity} />} />}
                            {typeof a.material === "boolean" && <DetailField label="Material change" value={a.material ? "Yes" : "No"} />}
                            {a.summary && <DetailField full label="Summary" value={a.summary} />}
                            {Array.isArray(a.changed_fields) && a.changed_fields.length > 0 && (
                              <DetailField full label="Changed fields">
                                <div className="flex flex-wrap gap-1">
                                  {a.changed_fields.map((f) => (
                                    <span key={f} className="inline-block px-1.5 py-0.5 rounded bg-secondary text-[10px] font-mono">{f}</span>
                                  ))}
                                </div>
                              </DetailField>
                            )}
                            {a.reason && <DetailField full label="Reason" value={a.reason} />}
                            {(a.ip_address || a.user_agent || a.session_fingerprint) && (
                              <DetailField full label="Request metadata">
                                <div className="text-[11px] font-mono text-muted-foreground space-y-0.5">
                                  {a.ip_address && <div>IP: {a.ip_address}</div>}
                                  {a.user_agent && <div className="truncate" title={a.user_agent}>UA: {a.user_agent}</div>}
                                  {a.session_fingerprint && <div>Session: {a.session_fingerprint}</div>}
                                </div>
                              </DetailField>
                            )}
                            <DetailField full label="Before value">
                              <pre className="bg-card border rounded p-2 text-[11px] overflow-auto max-h-[200px]">{fmt(a.before_value)}</pre>
                            </DetailField>
                            <DetailField full label="After value">
                              <pre className="bg-card border rounded p-2 text-[11px] overflow-auto max-h-[200px]">{fmt(a.after_value)}</pre>
                            </DetailField>
                            {hasVisibleNotes(a.notes) && <DetailField full label="Notes" value={formatNotes(a.notes, { fallback: "\u2014" })} />}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Audit records support internal accountability and compliance review · Not legal advice or final legal clearance · Internal-only access.
      </Disclaimer>
    </div>
  );
}

function DetailField({ label, value, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs">{children || value}</div>
    </div>
  );
}
